/**
 * Draining the outbox, and the one rule that governs it: nothing is ever
 * dropped.
 *
 * The only response that retires an event is a 200 naming it in `known` or in
 * `rejected`. Everything else, a 422, a 500, a 409, a refusal, a network that
 * never answered, means HOLD and try again. That asymmetry is deliberate and it
 * is the whole safety property: an event stuck in the outbox is an
 * inconvenience, an event dropped from it is a play that happened in front of
 * parents and is gone.
 *
 * `rejected` retires too, and it is not a contradiction of that rule. It names
 * an id the server already holds under a DIFFERENT game (ADR-0054), which no
 * retry can ever land here: resending it is the same request forever, and on a
 * FIFO outbox that is a stopped queue behind one id rather than one stuck play.
 * The server never says whose it is, only that it is spoken for.
 *
 * Re-sending is always safe because idempotency is keyed on the device's event
 * id (ADR-0041), so the retry costs nothing but a request.
 */

import { MAX_SYNC_BATCH, type SyncResponse } from '@good-season/schema';
import { network, storage } from '@good-season/platform';
import { diagnosticFor } from '@good-season/ui';
import { ApiError, OfflineError, post } from '../api/client.js';
import {
  OutboxSupersededError,
  OutboxUnavailableError,
  markSynced,
  pendingEvents,
  type LoggedEvent,
} from './db.js';

export const DRAIN_INTERVAL_MS = 5_000;

/**
 * How long one sync post may wait for an answer before it is given up as
 * offline. A gym network that accepts the connection and never replies would
 * otherwise hold `running` forever: every later drain returns early, the strip
 * reads "Sending" for good and the Finish gate waits on it.
 */
export const SYNC_TIMEOUT_MS = 15_000;

/** The smallest batch a timeout will shrink to. A 413 goes down to one (ADR-0112). */
export const MIN_SYNC_BATCH = 25;

/** The scorebook sync's prefix on a diagnostic code, so support knows which path. */
const SURFACE = 'SY';

/**
 * Why the last attempt did not get through, or null when it did.
 *
 * Every value here means the batch was kept. There is no variant for "gave up",
 * because there is no such outcome.
 */
export type SyncTrouble = 'offline' | 'refused' | 'finalized' | 'signedOut';

/** A game's trouble, with the short code a coach can read off the screen when there is one. */
export interface SyncFailure {
  readonly trouble: SyncTrouble;
  /** Set for `refused` and `signedOut`, the two a coach may need support for. */
  readonly diagnostic?: string | undefined;
}

export type SyncStatus =
  | { readonly kind: 'synced' }
  | { readonly kind: 'syncing'; readonly pending: number }
  | { readonly kind: 'pending'; readonly pending: number }
  | {
      readonly kind: 'held';
      readonly pending: number;
      readonly trouble: SyncTrouble;
      readonly diagnostic?: string | undefined;
    }
  /**
   * The store on this phone could not be read, so nothing is known about the
   * outbox, including how many are in it. Reported rather than swallowed: a
   * tick that failed silently left the indicator on its initial "synced",
   * which is the one sentence BUILD_PLAN 5.8 forbids being wrong. Found by the
   * fresh-context pass.
   */
  | { readonly kind: 'broken' }
  /**
   * A newer app version in another tab took the database. Nothing is wrong
   * with the store and a retry cannot help until the page reloads, so this is
   * not 'broken'. The outbox is untouched.
   */
  | {
      readonly kind: 'superseded';
      readonly diagnostic: string;
      /** See `OutboxSupersededError.storeMayBeGone`. */
      readonly storeMayBeGone: boolean;
    }
  /**
   * The browser wiped this phone's copy of the book under the page. Plays not
   * yet on the server are gone from the phone, and no retry can bring them
   * back, so this is neither 'broken' (which offers Try now) nor 'synced'.
   */
  | { readonly kind: 'unavailable'; readonly diagnostic: string };

/**
 * One request's worth of outbox, per game.
 *
 * The sync endpoint is per-game, so a device holding two games' events (a
 * tournament morning) needs one request each rather than one merged batch.
 * Capped at `MAX_SYNC_BATCH`: the cap exists because the inbound body is
 * attacker-controlled, and a client that exceeds it gets a 422 it must then
 * hold forever.
 *
 * Order is preserved from the caller, which sorts by `clientSeq`. Sending a
 * void before the play it names would be refused `unknown_void_target`, and
 * while that refusal is held rather than dropped, it is a batch that can never
 * succeed until the ordering is right.
 */
export function planBatches(
  pending: readonly LoggedEvent[],
  max: number | ((gameId: string) => number) = MAX_SYNC_BATCH,
): readonly { readonly gameId: string; readonly events: readonly LoggedEvent[] }[] {
  const byGame = new Map<string, LoggedEvent[]>();
  for (const row of pending) {
    const bucket = byGame.get(row.gameId);
    if (bucket === undefined) byGame.set(row.gameId, [row]);
    else bucket.push(row);
  }
  return [...byGame].map(([gameId, events]) => ({
    gameId,
    events: events.slice(0, typeof max === 'number' ? max : max(gameId)),
  }));
}

/**
 * What a failed attempt means for the indicator. It never means "discard".
 *
 * A 409 is called out separately because it is the one the coach can act on:
 * the game was finished and the plays are waiting for a reopen (ADR-0047),
 * which is a different sentence to say than "no signal".
 *
 * A signed-out session is the other one. The API answers an unauthenticated
 * request with a 401 carrying `AUTH_REQUIRED` (no cookie, or one that names no
 * session, `apps/api/src/http/tenant.ts`) or `AUTH_SESSION_EXPIRED`. Retrying
 * can never succeed until the coach signs in, so it must not read as "still
 * trying". Matched on the code and not the bare status: a 401 from a proxy or a
 * captive portal carries no such code and is not a statement about the session.
 */
export function troubleFor(error: unknown): SyncTrouble {
  if (error instanceof OfflineError) return 'offline';
  if (error instanceof ApiError) {
    if (error.code === 'GAME_FINALIZED') return 'finalized';
    if (error.code === 'AUTH_REQUIRED' || error.code === 'AUTH_SESSION_EXPIRED') {
      return 'signedOut';
    }
  }
  return 'refused';
}

function failureFor(error: unknown): SyncFailure {
  const trouble = troubleFor(error);
  return trouble === 'refused' || trouble === 'signedOut'
    ? { trouble, diagnostic: diagnosticFor(error, SURFACE) }
    : { trouble };
}

/** An id list as the contract states it. Anything else is not the contract. */
function isIdList(value: unknown): value is readonly string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

/**
 * Drain once, and report what went wrong, per game.
 *
 * Each game's batch is attempted independently. One game being finalised must
 * not stop another game's plays from landing, which a single try/catch around
 * the whole loop would have done. The answer is keyed by game for the same
 * reason: the engine reports to ONE book, and the first trouble of any game
 * once stood for all of them, so a morning game finished elsewhere put "This
 * game was finished. Reopen it to send them" on the afternoon game's book,
 * whose reopen reached the wrong game. Found by the fresh-context pass.
 */
export async function drainOnce(
  sizes: Map<string, number> = new Map(),
): Promise<ReadonlyMap<string, SyncFailure>> {
  const trouble = new Map<string, SyncFailure>();
  const pending = await pendingEvents();
  if (pending.length === 0) return trouble;

  const sizeFor = (gameId: string): number => sizes.get(gameId) ?? MAX_SYNC_BATCH;
  for (const batch of planBatches(pending, sizeFor)) {
    try {
      const answer = await post<SyncResponse>(
        `/api/scorebook/games/${batch.gameId}/events`,
        { events: batch.events.map((row) => row.event) },
        { timeoutMs: SYNC_TIMEOUT_MS },
      );
      /*
       * A 200 whose body is not the contract is a proxy, a captive portal or a
       * newer server, and it is HELD like any other failure. The contract is
       * `{ accepted, known, rejected }`, so a body missing either retiring
       * bucket is not it.
       *
       * Checked rather than trusted because the failure mode of trusting it is
       * the worst one available here: `markSynced(undefined)` throws inside the
       * try, the catch classifies the TypeError as a refusal, and the outbox
       * holds correctly for a reason that has nothing to do with the server.
       * Silent, and it would have made every "synced" transition unreachable.
       */
      if (!isIdList(answer.known) || !isIdList(answer.rejected)) {
        trouble.set(batch.gameId, {
          trouble: 'refused',
          diagnostic: diagnosticFor(new Error('sync response is not the contract'), SURFACE),
        });
        continue;
      }
      // `known` and `rejected` partition what was sent, and both retire. An id
      // in neither (a server not keeping the contract) stays for the next tick.
      await markSynced([...answer.known, ...answer.rejected]);
      // Grow back gradually: resetting to the maximum on a link that is still
      // slow costs a run of guaranteed timeouts after every success. Only a
      // batch that filled the stored size proves the link can carry that size,
      // so a smaller success (live scoring posts a few events per tick) leaves
      // it alone. On a link that stays slow, the probe costs at most one
      // timeout per success.
      const current = sizes.get(batch.gameId);
      if (current !== undefined && batch.events.length >= current) {
        const grown = current * 2;
        if (grown >= MAX_SYNC_BATCH) sizes.delete(batch.gameId);
        else sizes.set(batch.gameId, grown);
      }
    } catch (error) {
      // Only a deadline or a 413 shrinks the next attempt. Re-posting the
      // identical batch on a slow link aborts mid-upload forever, and a body
      // over a parser's cap is refused forever (ADR-0050, ADR-0112). A
      // no-network rejection or any other refusal says nothing about size.
      if (error instanceof OfflineError && error.timedOut) {
        // Never above the size already in use: after a 413 walked it under the
        // floor, raising it back to 25 replays the 413s.
        const floored = Math.max(MIN_SYNC_BATCH, Math.floor(batch.events.length / 2));
        sizes.set(batch.gameId, Math.min(sizeFor(batch.gameId), floored));
      } else if (error instanceof ApiError && error.status === 413) {
        // Down to one, not to the timeout floor: a cap that 25 events still
        // exceed would otherwise hold them, and every play behind them, forever.
        sizes.set(batch.gameId, Math.max(1, Math.floor(batch.events.length / 2)));
      }
      // Kept, always. Re-sending is safe precisely because the server keys on
      // the id this device minted.
      trouble.set(batch.gameId, failureFor(error));
    }
  }

  return trouble;
}

/** What a refused store read means for the indicator. */
export function unreadable(error: unknown): SyncStatus {
  if (error instanceof OutboxSupersededError) {
    return {
      kind: 'superseded',
      diagnostic: error.diagnostic,
      storeMayBeGone: error.storeMayBeGone,
    };
  }
  if (error instanceof OutboxUnavailableError) {
    return { kind: 'unavailable', diagnostic: error.diagnostic };
  }
  return { kind: 'broken' };
}

export interface SyncEngine {
  readonly stop: () => void;
  /** Drain now rather than at the next tick, for a reconnect or a manual retry. */
  readonly drainNow: () => void;
}

/**
 * Start draining: every five seconds while online, and immediately on reconnect.
 *
 * The reconnect trigger is the one that matters at a gym. A five second timer
 * alone means a coach who walks back into signal waits up to five seconds for
 * the indicator to stop saying offline, and the phone may be about to sleep.
 *
 * Drains EVERY game on the device, and reports on ONE: the book that started
 * it. The count and the trouble the indicator shows are this game's, as the
 * Finish gate's count is (`unsyncedCount`), so another game's stuck outbox
 * neither shows up here nor blocks this book. That other game's plays are
 * seen in its own book and nowhere else, which ADR-0066 records as an
 * accepted deviation together with the drain's team scope.
 */
export function startSync(gameId: string, onStatus: (status: SyncStatus) => void): SyncEngine {
  let stopped = false;
  let running = false;
  // In memory only, per engine. Not persisted across a reload: a reload is
  // likely a different link, relearning costs a few 15s ticks, and a stored
  // small size could outlive the slow link and throttle a good one.
  const batchSizes = new Map<string, number>();
  const mine = (rows: readonly LoggedEvent[]): number =>
    rows.reduce((total, row) => total + (row.gameId === gameId ? 1 : 0), 0);

  async function tick(): Promise<void> {
    if (stopped || running) return;
    running = true;
    try {
      const pending = await pendingEvents();
      const here = mine(pending);
      if (pending.length === 0) {
        onStatus({ kind: 'synced' });
        return;
      }

      if (!network.isOnline()) {
        onStatus(
          here === 0 ? { kind: 'synced' } : { kind: 'held', pending: here, trouble: 'offline' },
        );
        return;
      }

      if (here > 0) onStatus({ kind: 'syncing', pending: here });
      const failure = (await drainOnce(batchSizes)).get(gameId);
      const left = mine(await pendingEvents());

      if (left === 0) onStatus({ kind: 'synced' });
      else if (failure === undefined) onStatus({ kind: 'pending', pending: left });
      else onStatus({ kind: 'held', pending: left, ...failure });
    } catch (error) {
      // The only way out of the try is the store refusing a read: `drainOnce`
      // catches its own network and server failures. Said, never swallowed.
      onStatus(unreadable(error));
    } finally {
      running = false;
    }
  }

  const timer = setInterval(() => void tick(), DRAIN_INTERVAL_MS);
  const unsubscribe = network.onChange((online) => {
    if (online) void tick();
    else
      void pendingEvents()
        .then((rows) => {
          const here = mine(rows);
          if (here > 0) onStatus({ kind: 'held', pending: here, trouble: 'offline' });
        })
        .catch((error: unknown) => onStatus(unreadable(error)));
  });

  void tick();

  return {
    stop: () => {
      stopped = true;
      clearInterval(timer);
      unsubscribe();
    },
    drainNow: () => void tick(),
  };
}

/**
 * Ask the host not to evict the book, and report what it said.
 *
 * A refusal is NORMAL in a plain browser tab and is granted silently on an
 * installed PWA, so this is surfaced quietly in the sync indicator rather than
 * raised as an alarm. It is surfaced at all because the consequence is a game
 * evicted mid-season, and a coach who knows can install the app.
 */
export async function requestPersistence(): Promise<'granted' | 'denied' | 'unsupported'> {
  if (!storage.isSupported()) return 'unsupported';
  if (await storage.isPersisted()) return 'granted';
  return (await storage.requestPersistence()) ? 'granted' : 'denied';
}
